/**
 * sync.js -- cloud sync via Supabase for save states (so a save made on one
 * device can be resumed on another) and ROMs (so a cartridge uploaded on one
 * device is available on another). Entirely opt-in: with no Supabase
 * credentials configured (see .env.example) this module is inert and the
 * app behaves exactly as it did before this file existed.
 *
 * Auth is email magic-link (Supabase's signInWithOtp): no password to
 * manage, no OAuth app to register. See supabase/schema.sql for the
 * `saves`/`roms` tables + Row Level Security policies + Storage buckets
 * this talks to -- run that once in the Supabase SQL Editor before using this.
 *
 * Design mirrors storage.js on purpose: `saves`/`roms` tables hold METADATA
 * only, matching the local IndexedDB stores field-for-field; the actual
 * bytes live in private Storage buckets, keyed "<user id>/<game id>/<slot>"
 * and "<user id>/<rom id>" respectively.
 *
 * The two sync DIFFERENTLY, deliberately: saves are small (under ~1MB) and
 * sync eagerly in both directions (pushDirtySaves + pullRemoteSaves, both
 * called by syncNow()). ROMs can be several MB, so only their METADATA syncs
 * automatically (pushDirtyRoms + listRemoteRoms) -- actual ROM bytes are
 * fetched lazily, one at a time, only when a user picks a cloud-only entry
 * in the library and downloadRom() is called for it.
 *
 * Conflict resolution for saves is last-write-wins by `updatedAt` -- fine
 * for a single-player save that's normally only played from one device at a
 * time, but there is no smarter merge if you genuinely save the same slot
 * from two devices before either syncs. ROMs are content-addressed (the
 * SHA-256 IS the id), so there's no conflict to resolve -- re-pushing
 * identical bytes from a second device just upserts the same row.
 *
 * Cheats sync the same way saves do (eagerly, both directions, last-write-
 * wins by `updatedAt`) since they're just as small -- but there's no blob at
 * all, so a `cheats` table row (see supabase/schema.sql) is the whole
 * record, with no matching Storage bucket.
 */

import { createClient } from '@supabase/supabase-js';
import * as storage from './storage.js';

const ENV_URL = import.meta.env.VITE_SUPABASE_URL;
const ENV_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const SAVES_BUCKET = 'saves';
const ROMS_BUCKET = 'roms';

/**
 * "Bring your own Supabase": the build-time VITE_SUPABASE_* vars are only a
 * DEFAULT. A user can point this app at their own project instead, stored
 * here per-browser, which is what makes a public deployment viable -- the
 * person hosting it doesn't end up carrying everyone else's storage and
 * bandwidth on one free-tier project.
 *
 * A custom config OVERRIDES the built-in one rather than only filling in
 * when it's absent, so the same deployment works whether it ships with
 * credentials or not, and a visitor can always opt out of the shared
 * project onto their own.
 *
 * Storing the key in localStorage is not a leak: a Supabase publishable
 * (anon) key is designed to be shipped to browsers, and what actually
 * protects the data is Row Level Security -- see supabase/schema.sql, which
 * a BYO user has to run once in their own project or every sync call will
 * fail on missing tables.
 */
const CONFIG_KEY = 'snes9x-web:supabase-config';
let client = null;
let customConfig = readCustomConfig();

function readCustomConfig() {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.url && parsed?.key ? { url: parsed.url, key: parsed.key } : null;
  } catch {
    // Private mode, blocked site data, or corrupt JSON -- fall back to env.
    return null;
  }
}

/** The credentials in effect, or null if this app has no Supabase at all. */
export function getConfig() {
  if (customConfig) return { ...customConfig, custom: true };
  if (ENV_URL && ENV_KEY) return { url: ENV_URL, key: ENV_KEY, custom: false };
  return null;
}

/** Whether the built-in (env) credentials exist, regardless of any override. */
export function hasBuiltInConfig() {
  return Boolean(ENV_URL && ENV_KEY);
}

/**
 * Point this browser at a different Supabase project. Throws on obviously
 * wrong input rather than failing later inside an opaque network error.
 */
export function setCustomConfig(url, key) {
  const trimmedUrl = String(url ?? '').trim().replace(/\/+$/, '');
  const trimmedKey = String(key ?? '').trim();
  if (!/^https:\/\/[^\s/]+$/.test(trimmedUrl)) {
    throw new Error('Project URL should look like https://yourproject.supabase.co');
  }
  if (trimmedKey.length < 20) throw new Error('That publishable/anon key looks too short');
  customConfig = { url: trimmedUrl, key: trimmedKey };
  localStorage.setItem(CONFIG_KEY, JSON.stringify(customConfig));
  resetClient();
}

/** Drop the override and go back to whatever the build shipped with (if anything). */
export function clearCustomConfig() {
  customConfig = null;
  try {
    localStorage.removeItem(CONFIG_KEY);
  } catch {
    /* nothing to clear */
  }
  resetClient();
}

/**
 * Forget the memoised client so the next call builds one against the current
 * config. Each Supabase client keeps its own auth session keyed by project
 * ref, so switching projects means signing out of the old one here rather
 * than leaving a stale session attached to a client nobody uses again.
 */
function resetClient() {
  client = null;
}

export function isConfigured() {
  return Boolean(getConfig());
}

function getClient() {
  const config = getConfig();
  if (!config) return null;
  if (!client) {
    // Supabase's auth-js defaults to flowType: 'implicit', but Supabase
    // Auth's current default email templates generate PKCE-style magic
    // links (a `?code=` redirect, not the older `#access_token=` hash).
    // detectSessionInUrl (on by default) silently fails to establish a
    // session when the client's flowType doesn't match the URL's -- it
    // throws internally and there is no visible error, which is exactly
    // what "the link takes me to the page but I'm not signed in" looks
    // like. Setting this explicitly is what makes the redirect actually
    // get exchanged for a session.
    client = createClient(config.url, config.key, { auth: { flowType: 'pkce' } });
  }
  return client;
}

/**
 * A tight, per-call deadline distinct from main.js's overall 30s
 * withTimeout() around the whole sync operation. That outer timeout alone
 * turned an indefinite hang into a bounded one, but a single generic "Sync
 * timed out after 30s" doesn't say WHICH call never returned -- getSession(),
 * a refresh, or one of the actual PostgREST/Storage requests all look
 * identical from the outside otherwise. Every call below is labelled, so
 * whichever one is actually stuck names itself in the error.
 */
const CALL_DEADLINE_MS = 8000;

function withDeadline(promise, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} took longer than ${CALL_DEADLINE_MS / 1000}s to respond`)), CALL_DEADLINE_MS);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export async function getSession() {
  const c = getClient();
  if (!c) return null;
  const { data } = await withDeadline(c.auth.getSession(), 'getSession()');
  return data.session;
}

/** True for an HTTP 401 from PostgREST or Storage -- an expired/invalid access token, not an RLS denial (those come back as empty data or a 403). */
function isAuthError(error) {
  if (!error) return false;
  return error.status === 401 || error.statusCode === '401' || /jwt/i.test(error.message ?? '');
}

/**
 * Run one Supabase call (PostgREST or Storage, both resolve to {data, error})
 * and, if it fails on an expired/invalid token, force a session refresh and
 * retry exactly once. auth-js's own getSession() already refreshes proactively
 * when a session is near expiry, but there is a real race it documents itself:
 * a token that reads as valid client-side can still expire by the time the
 * request actually reaches the server (clock skew, network latency, a
 * backgrounded tab). Without this, that race surfaces as a 401 on whatever
 * happened to be the first sync call to run -- which is exactly what this
 * fixes, rather than leaving every call site to fend for itself.
 */
async function withAuthRetry(fn, label) {
  const first = await withDeadline(fn(), label);
  if (!isAuthError(first.error)) return first;

  const c = getClient();
  const { error: refreshError } = await withDeadline(c.auth.refreshSession(), 'refreshSession()');
  if (refreshError) return first; // refresh itself failed (e.g. truly signed out) -- surface the original error

  return withDeadline(fn(), `${label} (retry)`);
}

/** @param {(session: import('@supabase/supabase-js').Session | null) => void} callback */
export function onAuthStateChange(callback) {
  const c = getClient();
  if (!c) return () => {};
  const {
    data: { subscription },
  } = c.auth.onAuthStateChange((_event, session) => callback(session));
  return () => subscription.unsubscribe();
}

/** Send a magic-link sign-in email. The link brings the user back to this same page. */
export async function signInWithEmail(email) {
  const c = getClient();
  if (!c) throw new Error('Supabase is not configured (missing VITE_SUPABASE_URL/VITE_SUPABASE_PUBLISHABLE_KEY)');
  const { error } = await c.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: window.location.origin + window.location.pathname },
  });
  if (error) throw error;
}

export async function signOut() {
  const c = getClient();
  if (!c) return;
  const { error } = await c.auth.signOut();
  if (error) throw error;
}

function savePath(userId, gameId, slot) {
  return `${userId}/${gameId}/${slot}`;
}

function romPath(userId, romId) {
  return `${userId}/${romId}`;
}

/**
 * Push every locally-dirty save state (storage.listUnsyncedSaveIds()) up to
 * Supabase: the blob to Storage, the metadata to the `saves` table, then
 * mark it synced locally so it isn't pushed again next time.
 *
 * Deliberately lists dirty IDS and fetches each record inside the loop,
 * rather than asking storage for the dirty records themselves: reading
 * every save's blob up front to decide which ones are dirty is what made
 * this fail outright on a phone with a large local library -- see
 * storage.listUnsyncedSaveIds().
 */
export async function pushDirtySaves(onProgress) {
  const c = getClient();
  if (!c) return { pushed: 0 };
  const session = await getSession();
  if (!session) return { pushed: 0 };

  const dirtyIds = await storage.listUnsyncedSaveIds();
  onProgress?.({ phase: 'push-saves', done: 0, total: dirtyIds.length });
  let pushed = 0;

  for (const id of dirtyIds) {
    const record = await storage.getSaveStateById(id);
    // Deleted between listing and pushing -- nothing to upload.
    if (!record) continue;
    const path = savePath(session.user.id, record.gameId, record.slot);

    const { error: uploadError } = await withAuthRetry(
      () => c.storage.from(SAVES_BUCKET).upload(path, record.data, { contentType: 'application/octet-stream', upsert: true }),
      'upload save blob',
    );
    if (uploadError) throw uploadError;

    const { error: rowError } = await withAuthRetry(
      () =>
        c.from('saves').upsert(
          {
            user_id: session.user.id,
            game_id: record.gameId,
            slot: record.slot,
            game_name: record.gameName,
            size: record.size,
            thumbnail: record.thumbnail ?? null,
            storage_path: path,
            created_at: new Date(record.createdAt).toISOString(),
            updated_at: new Date(record.updatedAt).toISOString(),
          },
          { onConflict: 'user_id,game_id,slot' },
        ),
      'upsert saves row',
    );
    if (rowError) throw rowError;

    await storage.markSaveSynced(record.id, Date.now());
    pushed++;
    onProgress?.({ phase: 'push-saves', done: pushed, total: dirtyIds.length, gameId: record.gameId, slot: record.slot });
  }

  return { pushed };
}

/**
 * Delete every remote save row (and its Storage blob) for one game's given
 * slots. Used when the user bulk-deletes local save states -- without this,
 * the next sync would just pull the "deleted" saves right back down again,
 * since pullRemoteSaves() has no way to tell "this device hasn't pushed yet"
 * apart from "the user deliberately deleted this" -- a local absence alone
 * never means delete-the-remote-copy anywhere else in this file, on purpose
 * (a fresh device with nothing local yet must NOT be treated as "delete
 * everything remote"), so this is a deliberate, explicit, caller-initiated
 * action rather than something pullRemoteSaves()/syncNow() ever infers.
 */
export async function deleteAllRemoteSaves(gameId, slots) {
  const c = getClient();
  if (!c || slots.length === 0) return { deletedRemote: 0 };
  const session = await getSession();
  if (!session) return { deletedRemote: 0 };

  const paths = slots.map((slot) => savePath(session.user.id, gameId, slot));
  // Best-effort: a slot that was never actually pushed has no Storage object
  // to remove, and that shouldn't block deleting the (possibly nonexistent)
  // table row either.
  await withAuthRetry(() => c.storage.from(SAVES_BUCKET).remove(paths), 'remove save blobs').catch(() => {});

  const { error, count } = await withAuthRetry(
    () => c.from('saves').delete({ count: 'exact' }).eq('user_id', session.user.id).eq('game_id', gameId).in('slot', slots),
    'delete saves rows',
  );
  if (error) throw error;
  return { deletedRemote: count ?? slots.length };
}

/**
 * Pull every remote save row for the signed-in user and merge into local
 * storage. A remote row wins (its blob is downloaded and written locally)
 * when it's strictly newer than the local record, or the local record
 * doesn't exist yet (a fresh device); a local row that's newer than its
 * remote counterpart is left alone -- pushDirtySaves() will push it up on
 * its own next time.
 */
export async function pullRemoteSaves(onProgress) {
  const c = getClient();
  if (!c) return { pulled: 0 };
  const session = await getSession();
  if (!session) return { pulled: 0 };

  onProgress?.({ phase: 'pull-saves', done: 0, total: null });
  const { data: rows, error } = await withAuthRetry(() => c.from('saves').select('*').eq('user_id', session.user.id), 'select saves');
  if (error) throw error;

  let pulled = 0;
  // One cheap index-only pass instead of a per-row storage.getSaveState():
  // deciding whether a remote row wins only needs the local updatedAt, and
  // reading a whole record for that deserialises its entire save blob --
  // the same waste that broke the push path on a large local library.
  const localUpdatedAt = await storage.listSaveUpdatedAtMap();

  for (const row of rows ?? []) {
    const remoteUpdatedAt = new Date(row.updated_at).getTime();
    const localAt = localUpdatedAt.get(storage.saveStateId(row.game_id, row.slot));
    if (localAt != null && localAt >= remoteUpdatedAt) continue;

    const { data: blob, error: downloadError } = await withAuthRetry(
      () => c.storage.from(SAVES_BUCKET).download(row.storage_path),
      'download save blob',
    );
    if (downloadError) throw downloadError;

    await storage.putSaveState(row.game_id, row.slot, await blob.arrayBuffer(), {
      gameName: row.game_name,
      thumbnail: row.thumbnail ?? null,
      createdAt: new Date(row.created_at).getTime(),
      updatedAt: remoteUpdatedAt,
      syncedAt: remoteUpdatedAt,
    });

    pulled++;
    onProgress?.({ phase: 'pull-saves', done: pulled, total: rows.length, gameId: row.game_id, slot: row.slot });
  }

  return { pulled };
}

/**
 * Push every never-synced local ROM (storage.listUnsyncedRoms()) up to
 * Supabase: the bytes to Storage, the metadata to the `roms` table, then
 * mark it synced locally. Unlike saves, a ROM is content-addressed and
 * otherwise immutable, so this is a one-time "has it ever been pushed?"
 * rather than an updatedAt comparison.
 */
export async function pushDirtyRoms(onProgress) {
  const c = getClient();
  if (!c) return { pushedRoms: 0 };
  const session = await getSession();
  if (!session) return { pushedRoms: 0 };

  const dirty = await storage.listUnsyncedRoms();
  onProgress?.({ phase: 'push-roms', done: 0, total: dirty.length });
  let pushedRoms = 0;

  for (const record of dirty) {
    const path = romPath(session.user.id, record.id);

    const { error: uploadError } = await withAuthRetry(
      () => c.storage.from(ROMS_BUCKET).upload(path, record.data, { contentType: 'application/octet-stream', upsert: true }),
      'upload ROM bytes',
    );
    if (uploadError) throw uploadError;

    const { error: rowError } = await withAuthRetry(
      () =>
        c.from('roms').upsert(
          {
            user_id: session.user.id,
            rom_id: record.id,
            name: record.name,
            size: record.size,
            storage_path: path,
            added_at: new Date(record.addedAt).toISOString(),
          },
          { onConflict: 'user_id,rom_id' },
        ),
      'upsert roms row',
    );
    if (rowError) throw rowError;

    await storage.markRomSynced(record.id, Date.now());
    pushedRoms++;
    onProgress?.({ phase: 'push-roms', done: pushedRoms, total: dirty.length, romId: record.id, name: record.name });
  }

  return { pushedRoms };
}

/**
 * Metadata for every ROM synced from any device for this user -- no bytes.
 * The library UI merges this with the local ROM list to show "available in
 * the cloud, not on this device yet" entries without downloading anything
 * until the user actually asks for one (downloadRom()).
 */
export async function listRemoteRoms() {
  const c = getClient();
  if (!c) return [];
  const session = await getSession();
  if (!session) return [];

  const { data: rows, error } = await withAuthRetry(() => c.from('roms').select('*').eq('user_id', session.user.id), 'select roms');
  if (error) throw error;
  return rows ?? [];
}

/**
 * Download one ROM's bytes from Storage and write it into local IndexedDB,
 * marked already-synced (it just came FROM Supabase, so pushing it straight
 * back would be redundant). `row` is one entry from listRemoteRoms().
 */
export async function downloadRom(row) {
  const c = getClient();
  if (!c) throw new Error('Supabase is not configured');

  const { data: blob, error } = await withAuthRetry(() => c.storage.from(ROMS_BUCKET).download(row.storage_path), 'download ROM bytes');
  if (error) throw error;

  const bytes = new Uint8Array(await blob.arrayBuffer());
  return storage.saveRom(row.name, bytes, {
    addedAt: new Date(row.added_at).getTime(),
    syncedAt: Date.now(),
  });
}

/**
 * Push every locally-dirty cheat (storage.listUnsyncedCheats()) up to
 * Supabase's `cheats` table, then mark it synced. No Storage bucket
 * involved -- a cheat is just a small metadata row, id/code/description/
 * enabled, so the row IS the record.
 */
export async function pushDirtyCheats(onProgress) {
  const c = getClient();
  if (!c) return { pushedCheats: 0 };
  const session = await getSession();
  if (!session) return { pushedCheats: 0 };

  const dirty = await storage.listUnsyncedCheats();
  onProgress?.({ phase: 'push-cheats', done: 0, total: dirty.length });
  let pushedCheats = 0;

  for (const record of dirty) {
    // The `cheats` table's `id` column is a Postgres `uuid`; a record with
    // a non-UUID id (a real bug shipped briefly -- see storage.js's
    // isValidUuid()) would otherwise throw here and abort the rest of this
    // push. Skip it instead: it's left unsynced (storage.js's local cleanup
    // deletes ones like this on the next cheat-list refresh anyway) rather
    // than taking down every other cheat's sync with it.
    if (!storage.isValidUuid(record.id)) {
      console.warn('[sync] skipping cheat with a non-UUID id, will not sync:', record.id);
      continue;
    }
    const { error } = await withAuthRetry(
      () =>
        c.from('cheats').upsert({
          id: record.id,
          user_id: session.user.id,
          game_id: record.gameId,
          code: record.code,
          description: record.description,
          enabled: record.enabled,
          created_at: new Date(record.createdAt).toISOString(),
          updated_at: new Date(record.updatedAt).toISOString(),
        }),
      'upsert cheats row',
    );
    if (error) throw error;

    await storage.markCheatSynced(record.id, Date.now());
    pushedCheats++;
    onProgress?.({ phase: 'push-cheats', done: pushedCheats, total: dirty.length, gameId: record.gameId });
  }

  return { pushedCheats };
}

/**
 * Pull every remote cheat row for the signed-in user and merge into local
 * storage, keyed by the SAME id on both sides (see storage.putCheat()) so a
 * cheat that already exists locally gets updated in place instead of
 * duplicated. Same last-write-wins-by-`updatedAt` rule as pullRemoteSaves().
 */
export async function pullRemoteCheats(onProgress) {
  const c = getClient();
  if (!c) return { pulledCheats: 0 };
  const session = await getSession();
  if (!session) return { pulledCheats: 0 };

  onProgress?.({ phase: 'pull-cheats', done: 0, total: null });
  const { data: rows, error } = await withAuthRetry(() => c.from('cheats').select('*').eq('user_id', session.user.id), 'select cheats');
  if (error) throw error;

  let pulledCheats = 0;

  for (const row of rows ?? []) {
    const remoteUpdatedAt = new Date(row.updated_at).getTime();
    const local = await storage.getCheat(row.id);
    if (local && local.updatedAt >= remoteUpdatedAt) continue;

    await storage.putCheat(row.id, row.game_id, row.code, row.description, row.enabled, {
      createdAt: new Date(row.created_at).getTime(),
      updatedAt: remoteUpdatedAt,
      syncedAt: remoteUpdatedAt,
    });

    pulledCheats++;
    onProgress?.({ phase: 'pull-cheats', done: pulledCheats, total: rows.length, gameId: row.game_id });
  }

  return { pulledCheats };
}

/**
 * Push everything dirty (saves, ROMs, cheats), then pull everything new
 * (saves and cheats; ROM metadata is separate, see listRemoteRoms()).
 */
export async function syncNow(onProgress) {
  const pushSaves = await pushDirtySaves(onProgress);
  const pushRoms = await pushDirtyRoms(onProgress);
  const pushCheats = await pushDirtyCheats(onProgress);
  const pullSaves = await pullRemoteSaves(onProgress);
  const pullCheats = await pullRemoteCheats(onProgress);
  return { ...pushSaves, ...pushRoms, ...pushCheats, ...pullSaves, ...pullCheats };
}

/**
 * Disaster recovery: wipe every local record (ROMs, saves, cheats) and
 * re-download everything fresh from Supabase, ignoring whatever's currently
 * on disk entirely. Deliberately different from syncNow(): the normal
 * incremental pull trusts local data enough to leave a record alone when it
 * looks as new or newer than the remote copy (storage.getSaveState()/
 * getCheat() are still consulted by pullRemoteSaves()/pullRemoteCheats(),
 * but against an EMPTY database post-wipe, so every remote row wins
 * unconditionally) -- exactly wrong when local storage itself is the thing
 * that's suspect (e.g. a corrupted/hung IndexedDB), which is the only
 * scenario this exists for. ROMs are handled differently here too: normal
 * sync only lists cloud ROMs and downloads on demand (they can be several
 * MB each); force-pull eagerly downloads every one, since the whole point
 * is restoring a device to a known-good state in one action.
 */
export async function forcePull(onProgress) {
  const c = getClient();
  if (!c) throw new Error('Supabase is not configured');
  const session = await getSession();
  if (!session) throw new Error('Not signed in');

  onProgress?.({ phase: 'wipe' });
  await storage.wipeLocalDatabase();

  const pullSaves = await pullRemoteSaves(onProgress);
  const pullCheats = await pullRemoteCheats(onProgress);

  onProgress?.({ phase: 'list-roms', done: 0, total: null });
  const remoteRoms = await listRemoteRoms();
  let pulledRoms = 0;
  for (const row of remoteRoms) {
    await downloadRom(row);
    pulledRoms++;
    onProgress?.({ phase: 'pull-roms', done: pulledRoms, total: remoteRoms.length, name: row.name });
  }

  return { ...pullSaves, ...pullCheats, pulledRoms };
}
